import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="mb-12">
            <p className="font-manrope text-sm text-primary font-semibold uppercase tracking-widest mb-3">
              Legal
            </p>
            <h1 className="font-oswald uppercase text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              Privacy Policy
            </h1>
            <p className="font-manrope text-sm text-gray-500 mt-4">
              Effective Date: June 27, 2025
            </p>
            <div className="w-16 h-1 bg-primary mt-6" />
          </div>

          {/* Intro */}
          <div className="prose-content space-y-8">
            <p className="font-manrope text-gray-700 leading-relaxed">
              Welcome to <strong>Loose Leaf Media</strong> ("we," "us," or "our"). We operate the website{' '}
              <strong>looseleafagency.com</strong> and related landing pages (collectively, the "Site"), through which we offer
              digital products, including eBooks and prompt guides. This Privacy Policy explains how we collect, use, disclose,
              and protect your personal information when you visit our Site or make a purchase.
            </p>
            <p className="font-manrope text-gray-700 leading-relaxed">
              By using our Site, you agree to the practices described in this Privacy Policy. If you do not agree, please
              discontinue use of the Site.
            </p>

            <Section title="1. Information We Collect">
              <p className="font-manrope text-gray-700 leading-relaxed">We collect the following categories of information:</p>
              <Subsection title="a) Information You Provide Directly">
                <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700">
                  <li>Full name and email address (when you purchase or contact us)</li>
                  <li>Payment information (processed securely by Stripe — we do not store card details)</li>
                  <li>Communications you send us, including support requests</li>
                </ul>
              </Subsection>
              <Subsection title="b) Information Collected Automatically">
                <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700">
                  <li>IP address and approximate geographic location</li>
                  <li>Browser type, device type, and operating system</li>
                  <li>Pages visited, time spent on pages, and referring URLs</li>
                  <li>Clickstream data and interaction events</li>
                  <li>Cookies and similar tracking technologies (see Section 5)</li>
                </ul>
              </Subsection>
              <Subsection title="c) Information from Third Parties">
                <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700">
                  <li>Google Analytics: aggregated traffic and behaviour data</li>
                  <li>Google Ads: ad interaction and conversion data</li>
                  <li>Stripe: payment confirmation and transaction identifiers</li>
                  <li>Meta (Facebook/Instagram) Ads (if applicable): ad engagement data</li>
                </ul>
              </Subsection>
            </Section>

            <Section title="2. How We Use Your Information">
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700">
                <li>To process and fulfil your digital product purchases</li>
                <li>To send purchase confirmations, receipts, and product delivery emails</li>
                <li>To respond to customer service inquiries</li>
                <li>To improve our website content, user experience, and product offerings</li>
                <li>To measure and optimise the performance of our advertising campaigns</li>
                <li>To detect and prevent fraud or security breaches</li>
                <li>To comply with applicable legal obligations</li>
                <li>To send occasional marketing communications (only where you have opted in or where permitted by law)</li>
              </ul>
            </Section>

            <Section title="3. Legal Basis for Processing (GDPR)">
              <p className="font-manrope text-gray-700 leading-relaxed">
                For users in the European Economic Area (EEA) or United Kingdom, we process personal data on the following legal bases:
              </p>
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-3">
                <li><strong>Contract:</strong> processing necessary to fulfil your purchase</li>
                <li><strong>Legitimate interests:</strong> analytics, fraud prevention, and improving our services</li>
                <li><strong>Consent:</strong> marketing communications and non-essential cookies (where we obtain your consent)</li>
                <li><strong>Legal obligation:</strong> compliance with applicable law</li>
              </ul>
            </Section>

            <Section title="4. Sharing Your Information">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We do not sell, rent, or trade your personal information. We share your information only with trusted third parties
                that help us operate our business, under strict data processing agreements:
              </p>
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-3">
                <li>Stripe, Inc. — payment processing</li>
                <li>Google LLC — analytics and advertising</li>
                <li>Resend or similar email service provider — transactional email delivery</li>
                <li>Vercel, Inc. or our web hosting provider — website infrastructure</li>
                <li>Meta Platforms, Inc. — advertising (if we run Meta Ads)</li>
              </ul>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                We may also disclose your information if required by law, court order, or to protect our legal rights.
              </p>
            </Section>

            <Section title="5. Cookies and Tracking Technologies">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We use cookies and similar technologies to operate and improve our Site. The types of cookies we use include:
              </p>
              <div className="space-y-2 mt-3 font-manrope text-gray-700">
                <p><strong>Essential Cookies:</strong> Required for the Site to function. Cannot be disabled.</p>
                <p><strong>Analytics Cookies:</strong> Google Analytics tracks usage patterns to help us improve our content and experience.</p>
                <p><strong>Advertising Cookies:</strong> Google Ads and Meta Pixel track conversions and enable us to show relevant ads to potential customers. These cookies may track you across websites.</p>
                <p><strong>Preference Cookies:</strong> Remember your settings and preferences.</p>
              </div>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                By continuing to use our Site, you consent to our use of cookies as described in this policy.
              </p>
            </Section>

            <Section title="6. Data Retention">
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700">
                <li>Purchase and transaction records: retained for 7 years for tax and legal compliance</li>
                <li>Email communications: retained for 3 years</li>
                <li>Analytics data: retained per the applicable platform's default retention policy</li>
                <li>Marketing data: retained until you withdraw consent or unsubscribe</li>
              </ul>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                After the applicable retention period, we securely delete or anonymise your data.
              </p>
            </Section>

            <Section title="7. Your Privacy Rights">
              <p className="font-manrope text-gray-700 leading-relaxed">
                Depending on your location, you may have the following rights regarding your personal data:
              </p>
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-3">
                <li><strong>Access:</strong> request a copy of the personal data we hold about you</li>
                <li><strong>Rectification:</strong> request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> request that we limit processing of your data</li>
                <li><strong>Data Portability:</strong> receive your data in a structured, machine-readable format</li>
                <li><strong>Object:</strong> object to processing based on legitimate interests or for direct marketing</li>
                <li><strong>Withdraw Consent:</strong> withdraw consent at any time where processing is based on consent</li>
              </ul>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:hello@looseleafagency.com" className="text-primary hover:text-primary/80 transition-colors">
                  hello@looseleafagency.com
                </a>
                . We will respond within 30 days.
              </p>
            </Section>

            <Section title="8. Children's Privacy">
              <p className="font-manrope text-gray-700 leading-relaxed">
                Our Site is not directed to children under the age of 13 (or 16 in the EEA). We do not knowingly collect personal
                information from children. If you believe we have inadvertently collected information from a child, please contact
                us immediately and we will delete it.
              </p>
            </Section>

            <Section title="9. International Data Transfers">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We are based internationally and our service providers may process your data in countries outside your own,
                including the United States. We take appropriate safeguards — such as standard contractual clauses — to protect
                your information in such transfers, in accordance with applicable data protection law.
              </p>
            </Section>

            <Section title="10. Security">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal information against
                unauthorised access, loss, destruction, or alteration. All payment processing is handled by Stripe, which is PCI
                DSS Level 1 certified. However, no transmission over the internet is 100% secure, and we cannot guarantee
                absolute security.
              </p>
            </Section>

            <Section title="11. Third-Party Links">
              <p className="font-manrope text-gray-700 leading-relaxed">
                Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content of
                those websites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </Section>

            <Section title="12. Changes to This Privacy Policy">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" at the top
                of this page. We encourage you to review this Policy periodically. Continued use of the Site after changes are
                posted constitutes your acceptance of the updated Policy.
              </p>
            </Section>

            <Section title="13. Contact Us">
              <p className="font-manrope text-gray-700 leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please
                contact us:
              </p>
              <div className="mt-4 font-manrope text-gray-700 space-y-1">
                <p><strong>Company:</strong> Loose Leaf Media</p>
                <p><strong>Website:</strong> looseleafagency.com</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:hello@looseleafagency.com" className="text-primary hover:text-primary/80 transition-colors">
                    hello@looseleafagency.com
                  </a>
                </p>
              </div>
            </Section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <p className="font-manrope text-sm text-gray-500">
                © 2026 Loose Leaf Media. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-gray-200 pt-8">
      <h2 className="font-oswald uppercase text-xl font-bold text-gray-900 tracking-wide mb-4">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Subsection({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="font-manrope text-sm font-bold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  );
}
